import type { UseQueryOptions, UseQueryResult } from '@tanstack/react-query'
import { useQuery, useQueryClient } from '@tanstack/react-query'

import {
  GET_AGGREGATE_AVAILABILITY_API_ID,
  getAggregateAvailability,
} from '@/features/bidding/endpoints/aggregateAvailability'
import { useErrorHandler } from '@/features/bidding/hooks/useErrorHandler'
import { buildAggregateAvailabilityResponse } from '@/features/bidding/mocks/aggregateAvailability'
import type { AggregateAvailabilityResponse } from '@/features/bidding/types/aggregateAvailability'
import type { AggregateMarket } from '@/features/bidding/types/aggregateSuggestedBid'
import type { MarketDate } from '@/features/bidding/utils/date/marketDate'

export type UseAggregateAvailabilityQueryParams = {
  deliveryDay: MarketDate
  portfolioCode: string
  market: AggregateMarket | null
  resourceCategory: string
}

export type UseAggregateAvailabilityQueryResult = Omit<UseQueryResult, 'data'> & {
  aggregateAvailability: AggregateAvailabilityResponse | null
}

export function useAggregateAvailabilityQuery(
  params: UseAggregateAvailabilityQueryParams,
  options?: Omit<UseQueryOptions<AggregateAvailabilityResponse>, 'queryKey' | 'queryFn'> & { useMockData?: boolean },
): UseAggregateAvailabilityQueryResult {
  const { deliveryDay, portfolioCode, market, resourceCategory } = params
  const useMockData = options?.useMockData ?? false

  const { data = null, ...queryResult } = useQuery({
    ...options,
    enabled: market !== null && (options?.enabled ?? true),
    queryKey: [GET_AGGREGATE_AVAILABILITY_API_ID, deliveryDay.toISODate(), portfolioCode, market, resourceCategory],
    queryFn: () =>
      useMockData
        ? Promise.resolve(buildAggregateAvailabilityResponse(deliveryDay))
        : getAggregateAvailability({
            deliveryDay: deliveryDay.toISODate(),
            portfolioCode,
            market: market!,
            resourceCategory,
          }),
  })

  useErrorHandler(queryResult.isError)

  return { aggregateAvailability: data, ...queryResult }
}

export function useInvalidateAggregateAvailabilityQuery() {
  const queryClient = useQueryClient()
  return () => queryClient.invalidateQueries({ queryKey: [GET_AGGREGATE_AVAILABILITY_API_ID] })
}
